import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Policy } from '@/types';
import { useChat } from '@/hooks/useChat';
import { getCustomerClaims, getCustomerPolicies } from '@/service/customerServices';
import { getPolicyClaimAction, rememberOpenClaim } from '@/utils/openClaims';

/**
 * "File a claim" for a policy: opens the claim already in progress, shows a submitted one,
 * or starts a new one in the claims chat. Same rules as the original PolicyCard
 * (pages/customer/Policies.tsx), extracted so every page shares one implementation.
 */
export function useFileClaim() {
  const navigate = useNavigate();
  const { handleOpenClaim } = useChat();
  const [busyPolicyId, setBusyPolicyId] = useState<string | null>(null);

  const openExistingClaim = async (policyId: string, existingClaimId?: string | null) => {
    const opened: { id?: string; claim_id?: string } = await handleOpenClaim({ policyId, claimId: existingClaimId || '' });
    const openedId = opened?.id || opened?.claim_id || existingClaimId;
    if (!openedId) return false;
    rememberOpenClaim(policyId, openedId);
    navigate(`/customer/chat/${openedId}`);
    return true;
  };

  const fileClaim = async (policy: Policy) => {
    if (busyPolicyId) return;
    setBusyPolicyId(policy.id);
    try {
      const [freshClaims, freshPolicies] = await Promise.all([getCustomerClaims(), getCustomerPolicies()]);
      const currentPolicy = freshPolicies.find((item) => String(item.id) === String(policy.id)) ?? policy;
      const decision = getPolicyClaimAction(currentPolicy, freshClaims);

      if (decision.action === 'view' && decision.claimId) {
        navigate(`/customer/claims/${decision.claimId}`);
        return;
      }
      if (!(await openExistingClaim(policy.id, decision.claimId))) {
        toast.error('Could not open claim. Try again.');
      }
    } catch (err) {
      type ClaimError = { id?: string; claim_id?: string; claimId?: string; mode?: string; message?: string; data?: ClaimError };
      const body: ClaimError = (err as { response?: { data?: ClaimError } })?.response?.data ?? (err as ClaimError) ?? {};
      const record = body?.data && typeof body.data === 'object' ? body.data : body;
      const existingId = record?.id || record?.claim_id || record?.claimId;
      const failedMode = String(record?.mode || body?.mode || '').toLowerCase();

      if (failedMode === 'submitted' && existingId) {
        navigate(`/customer/claims/${existingId}`);
        return;
      }
      if (existingId || failedMode === 'resume') {
        try {
          if (await openExistingClaim(policy.id, existingId)) return;
        } catch {
          // The claim could not be reopened; show the server message below.
        }
      }
      const message =
        (typeof body?.message === 'string' && body.message) ||
        (typeof record?.message === 'string' && record.message) ||
        'Could not open claim. Try again.';
      toast.error(message);
    } finally {
      setBusyPolicyId(null);
    }
  };

  return { fileClaim, busyPolicyId };
}
